import { createSelector } from "reselect";
import { isJust, Just } from "sanctuary";

/**
 * Builds a function around the Okta authentication object that can be used to log out,
 * clear the current session, and redirect to a specific URI. The post-logout URI must be
 * pre-configured in Okta within `Applications > Revenue Management > General Settings > LOGIN >
 * Sign-out redirect URIs`.
 *
 * Using the OktaAuth `signOut` method automatically clears tokens stored in the Okta token manager.
 * See: https://github.com/okta/okta-auth-js#signout
 *
 * @param {OktaAuth} oktaAuth - Okta authentication object created when the app starts
 * @param {object} config - configuration options to be used with the OktaAuth signOut method
 * @param {string} [config.redirectUri="/login"] - the router endpoint to which a user should be
 *   redirected upon signing out
 * @returns {Promise<boolean>} true or false depending on the success of the call
 */
export const logOut = async (oktaAuth, config = {}) => {
  let { redirectUri } = config;
  redirectUri ||= "/login";

  await oktaAuth.signOut({
    postLogoutRedirectUri: window.location.origin + redirectUri,
  });
};

export const getAuthToken = () => {
  // There may be a better way to get the okta access token
  // but a lot of the normal methods are async -AM
  const oktaTokenStorage =
    JSON?.parse(localStorage?.getItem("okta-token-storage")) ?? {};
  const { accessToken = "" } = oktaTokenStorage?.accessToken ?? {};

  return Just(accessToken);
};

export const userIsAuthenticated = createSelector(getAuthToken, isJust);
